import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useToast } from "../../components/ui/Toast";
import { SoftButton, SoftFooter, SoftTag, cls } from "../hatim/soft/SoftKit";
import QuestionRows from "../muzakere/QuestionRows";
import QuestionsSection from "../muzakere/QuestionsSection";
import { LIMITS, issueText, questionsIssue, toPayload } from "../muzakere/questionRules";
import {
  useCreatePackageMutation,
  useCreateProposalMutation,
  usePackageDetailQuery,
  useSubmitPackageMutation,
  useUpdatePackageMutation,
} from "../../api/libraryApi";
import {
  PACKAGE_ISSUE_KEY,
  PACKAGE_STEPS,
  PROPOSAL_MESSAGE_MAX,
  TAGS_MAX,
  appendQuestion,
  canAdvancePackage,
  packageForm,
  packageInfoIssue,
  parseTags,
} from "./libraryRules";
import { CenterMessage, LIBRARY_HOME, LibraryPanel, Spinner, inputCls, packagePath } from "./LibraryKit";
import QuestionSearchDialog from "./QuestionSearchDialog";

const STEP_TEXT = {
  info: ["lib_step_info", "lib_step_info_desc"],
  questions: ["lib_step_questions", "lib_step_questions_desc"],
  summary: ["lib_step_summary", "lib_step_summary_desc"],
};

/** Sunucu cevabı gelmedi mi (kayıt sunucuda tamamlanmış olabilir). */
const noResponse = (error) => error?.status === "FETCH_ERROR";

/**
 * Paket oluştur (/kutuphane/yeni) ya da düzenle (/kutuphane/:id/duzenle).
 * Mobil PackageEditWizard: Bilgi, Sorular, Özet; üstte adım noktaları, altta
 * sabit Geri ve İleri. Özel pakette "Onaya gönder"; çatalda "Kaydedince asıl
 * pakete öner" (mesajla). Kayıt cevapsız kalırsa yeniden gönderilmez.
 */
export default function PackageEditPage() {
  const { id } = useParams();
  const { t } = useScriptMode();
  const { data: existing, isError, error, refetch, fulfilledTimeStamp } = usePackageDetailQuery(
    { packageId: id },
    { skip: !id, refetchOnMountOrArgChange: true }
  );
  // Düzenleme önbellekteki eski halden değil, sayfa açıldıktan sonra gelen
  // güncel paketten başlar (eski halin üstüne yazılmasın).
  const openedAt = useRef(Date.now());
  const fresh = !!fulfilledTimeStamp && fulfilledTimeStamp >= openedAt.current;
  const title = t(id ? "lib_edit_package" : "lib_new_package_title");
  const back = id ? packagePath(id) : LIBRARY_HOME;

  if (id && !fresh && !isError) {
    return (
      <LibraryPanel title={title} back={back}>
        <Spinner />
      </LibraryPanel>
    );
  }
  if (id && (isError || !existing)) {
    return (
      <LibraryPanel title={title} back={back}>
        <CenterMessage text={error?.message || t("mzq_package_failed")} danger onRetry={refetch} error={error} />
      </LibraryPanel>
    );
  }
  if (id && (!existing.isMine || existing.locked)) {
    return (
      <LibraryPanel title={title} back={back}>
        <CenterMessage text={t("lib_not_owner")} />
      </LibraryPanel>
    );
  }
  return <PackageWizard key={id || "new"} existing={id ? existing : null} title={title} back={back} />;
}

function PackageWizard({ existing, title, back }) {
  const { t } = useScriptMode();
  const toast = useToast();
  const navigate = useNavigate();
  const [form, setForm] = useState(() => packageForm(existing));
  const [stepIndex, setStepIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [formError, setFormError] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  // Özel pakette (yeni ya da hiç gönderilmemiş) kaydederken onaya gönder.
  const canSubmit = !existing || existing.reviewState === "private";
  const [submitReview, setSubmitReview] = useState(false);
  const isFork = !!existing?.forkedFrom;
  const [propose, setPropose] = useState(false);
  const [proposeMessage, setProposeMessage] = useState("");
  const inFlight = useRef(false);

  const [createPackage] = useCreatePackageMutation();
  const [updatePackage] = useUpdatePackageMutation();
  const [submitPackage] = useSubmitPackageMutation();
  const [createProposal] = useCreateProposalMutation();

  const step = PACKAGE_STEPS[stepIndex];
  const isLast = stepIndex === PACKAGE_STEPS.length - 1;
  const tags = parseTags(form.tagsText);
  const ready = canAdvancePackage(step, form) && !saving && !uncertain;
  const infoIssue = packageInfoIssue({ title: form.title, description: form.description, tags });
  const qIssue = questionsIssue(form.questions);
  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Adım değişince eski kayıt hatası kalkar.
  useEffect(() => {
    setFormError(null);
  }, [stepIndex]);

  const save = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    let keepLocked = false;
    setSaving(true);
    setFormError(null);
    try {
      const body = {
        title: form.title.trim(),
        description: form.description.trim(),
        tags,
        questions: form.questions.map(toPayload),
      };
      const result = existing
        ? await updatePackage({ packageId: existing.id, ...body })
        : await createPackage({ ...body, submit: canSubmit && submitReview });
      if (result.error) {
        if (noResponse(result.error)) {
          keepLocked = true;
          setUncertain(true);
          return;
        }
        setFormError(result.error.message || t("mzq_package_save_failed"));
        return;
      }
      keepLocked = true;
      const saved = result.data?.data?.package;
      const savedId = saved?.id || existing?.id;
      const notes = [t("lib_saved")];
      if (existing && canSubmit && submitReview) {
        const r = await submitPackage({ packageId: existing.id, submitted: true });
        notes.push(r.error ? r.error.message || t("lib_action_failed") : t("lib_submitted"));
      }
      if (isFork && propose && existing) {
        const r = await createProposal({ forkId: existing.id, message: proposeMessage });
        notes.push(r.error ? r.error.message || t("lib_action_failed") : t("lib_proposal_sent"));
      }
      toast.success(notes.join(" "));
      navigate(savedId ? packagePath(savedId) : LIBRARY_HOME, { replace: true });
    } finally {
      if (!keepLocked) inFlight.current = false;
      setSaving(false);
    }
  };

  const next = () => {
    if (!ready) return;
    if (isLast) save();
    else setStepIndex((i) => i + 1);
  };
  const goBack = () => {
    if (saving) return;
    if (stepIndex === 0) navigate(back);
    else setStepIndex((i) => i - 1);
  };

  const [stepTitle, stepDesc] = STEP_TEXT[step];

  const renderInfo = () => (
    <div className="px-4 pb-4 flex flex-col gap-4">
      <label className="flex flex-col gap-1.5">
        <span className={`flex justify-between text-xs ${cls.muted}`}>
          <span>{t("lib_field_title")}</span>
          <span>{`${form.title.length}/${LIMITS.titleMax}`}</span>
        </span>
        <input
          value={form.title}
          onChange={(e) => update({ title: e.target.value })}
          maxLength={LIMITS.titleMax}
          disabled={saving}
          autoFocus
          className={inputCls}
        />
      </label>
      <label className="flex flex-col gap-1.5">
        <span className={`flex justify-between text-xs ${cls.muted}`}>
          <span>{t("lib_field_description")}</span>
          <span>{`${form.description.length}/${LIMITS.descriptionMax}`}</span>
        </span>
        <textarea
          value={form.description}
          onChange={(e) => update({ description: e.target.value })}
          maxLength={LIMITS.descriptionMax}
          rows={4}
          disabled={saving}
          className={inputCls}
        />
      </label>
      <label className="flex flex-col gap-1.5">
        <span className={`flex justify-between text-xs ${cls.muted}`}>
          <span>{t("lib_field_tags")}</span>
          <span>{`${tags.length}/${TAGS_MAX}`}</span>
        </span>
        <input
          value={form.tagsText}
          onChange={(e) => update({ tagsText: e.target.value })}
          maxLength={300}
          disabled={saving}
          className={inputCls}
        />
      </label>
      {tags.length ? (
        <div className="flex flex-wrap gap-1.5">
          {tags.map((tag) => (
            <SoftTag key={tag}>{tag}</SoftTag>
          ))}
        </div>
      ) : null}
      {infoIssue && form.title.trim() ? <p className={`text-sm ${cls.danger}`}>{t(PACKAGE_ISSUE_KEY[infoIssue])}</p> : null}
    </div>
  );

  const renderQuestions = () => (
    <div className="px-4 pb-4 flex flex-col gap-2">
      <QuestionsSection
        questions={form.questions}
        onChange={(questions) => update({ questions })}
        disabled={saving}
        onSearchQuestions={() => setSearchOpen(true)}
      />
      {qIssue?.code === "tooMany" ? <p className={`text-sm ${cls.danger}`}>{t(issueText("tooMany"))}</p> : null}
    </div>
  );

  const check = (checked, onChange, label) => (
    <label className={`flex items-center gap-2.5 text-base cursor-pointer ${cls.title}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={saving}
        className="w-4 h-4 accent-terakkiPrimaryDark"
      />
      {label}
    </label>
  );

  const renderSummary = () => {
    if (uncertain) return <p className={`px-4 pb-4 text-sm ${cls.danger}`}>{t("lib_uncertain")}</p>;
    return (
      <div className="flex flex-col gap-4 pb-4">
        <div className={`px-4 py-3 flex flex-col gap-1 ${cls.summary}`}>
          <span className={`text-base break-words ${cls.title}`}>{form.title.trim()}</span>
          <span className={`text-xs ${cls.muted}`}>
            {[`${form.questions.length} ${t("lib_word_question")}`, tags.join(", ")].filter(Boolean).join(" · ")}
          </span>
        </div>
        <div className="px-4 flex flex-col gap-1.5">
          {canSubmit ? (
            <>
              {check(submitReview, setSubmitReview, t("lib_submit"))}
              <p className={`text-sm ${cls.muted}`}>{t("lib_submit_hint")}</p>
            </>
          ) : (
            <p className={`text-sm ${cls.muted}`}>{t("lib_reverify_hint")}</p>
          )}
        </div>
        {isFork ? (
          <div className="px-4 flex flex-col gap-2">
            {check(propose, setPropose, t("lib_propose_on_save"))}
            {propose ? (
              <textarea
                value={proposeMessage}
                onChange={(e) => setProposeMessage(e.target.value)}
                maxLength={PROPOSAL_MESSAGE_MAX}
                rows={3}
                placeholder={t("lib_message_optional")}
                aria-label={t("lib_message_optional")}
                disabled={saving}
                className={inputCls}
              />
            ) : null}
          </div>
        ) : null}
        <div className="px-4 flex flex-col gap-1">
          <span className={`text-xs ${cls.muted}`}>{`${t("lib_questions")} · ${form.questions.length}`}</span>
          <QuestionRows questions={form.questions} showAnswers />
        </div>
      </div>
    );
  };

  return (
    <LibraryPanel
      Icon={EditNoteOutlinedIcon}
      title={title}
      back={back}
      footer={
        <SoftFooter>
          {uncertain ? (
            <SoftButton tone="soft" fullWidth onClick={() => navigate(LIBRARY_HOME)}>
              {t("lib_back_to_library")}
            </SoftButton>
          ) : (
            <>
              <SoftButton tone={stepIndex === 0 ? "cancel" : "soft"} fullWidth onClick={goBack} disabled={saving}>
                {t(stepIndex === 0 ? "muzakere_cancel" : "lib_prev")}
              </SoftButton>
              <SoftButton
                tone={isLast ? "success" : "primary"}
                fullWidth
                onClick={next}
                disabled={!ready}
                endIcon={saving ? null : isLast ? <CheckIcon /> : <ChevronRightIcon />}
              >
                {saving ? <CircularProgress size={18} color="inherit" /> : t(isLast ? "save" : "lib_next")}
              </SoftButton>
            </>
          )}
        </SoftFooter>
      }
    >
      <div className="flex justify-center gap-2 pt-4" aria-hidden>
        {PACKAGE_STEPS.map((key, index) => (
          <span
            key={key}
            className={`h-1.5 rounded-full transition-all ${
              index === stepIndex
                ? "w-6 bg-terakkiPrimaryDark dark:bg-[#8FC9CA]"
                : index < stepIndex
                  ? "w-1.5 bg-terakkiPrimary/60 dark:bg-[#8FC9CA]/60"
                  : "w-1.5 bg-terakkiBorder dark:bg-white/20"
            }`}
          />
        ))}
      </div>
      <div className="px-4 pt-3 pb-4">
        <h2 className={`text-lg font-Medium ${cls.title}`}>{`${stepIndex + 1}. ${t(stepTitle)}`}</h2>
        <p className={`text-sm mt-0.5 ${cls.muted}`}>{t(stepDesc)}</p>
      </div>
      {step === "info" ? renderInfo() : step === "questions" ? renderQuestions() : renderSummary()}
      {formError ? <p role="alert" className={`px-4 pb-4 text-sm ${cls.danger}`}>{formError}</p> : null}

      <QuestionSearchDialog
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onAdd={(q) => {
          const nextList = appendQuestion(form.questions, q);
          if (nextList === form.questions) return false;
          setForm((f) => ({ ...f, questions: appendQuestion(f.questions, q) }));
          return true;
        }}
      />
    </LibraryPanel>
  );
}
